<template>
	<v-card
		class="kanban-card mb-2"
		:class="{
			'kanban-card--clickable': clickable,
			'kanban-card--rework': hasReworkEdge,
		}"
		variant="outlined"
		@click="clickable ? $emit('click', item) : null"
	>
		<slot :item="item">
			<v-card-text>{{ item }}</v-card-text>
		</slot>
		<slot name="actions" :item="item" />
	</v-card>
</template>

<script>
export default {
	name: "KanbanCard",

	props: {
		item: {
			type: Object,
			required: true,
		},
		// DCTL-2257: the card's workflow state (a key of WORKFLOW_STATES). Boards
		// pass their card's real state and this component decides whether it earns
		// an edge, so the "rework only" rule below is enforced in one place instead
		// of being re-implemented per board. Replaces a `columnColor` hex passed
		// down from each board's own accent map.
		edgeState: {
			type: String,
			default: null,
		},
		clickable: {
			type: Boolean,
			default: false,
		},
	},

	emits: ["click", "action"],

	computed: {
		// design-system/COLOR.md, REV 2.1: "Rework is the only state that gets a
		// 4px left edge on a card. One state has a structural marker because
		// exactly one state has a required action. Nothing else on the axis may
		// take an edge." Deliberately not table-driven - the whole point is that
		// there is no list here to grow.
		//
		// This costs no information. Under the four-bucket ruling every other card
		// in a column takes the SAME hue as its siblings (Available all queued-grey,
		// My Completed all green, and the manager board is one state per column),
		// so the edge only ever discriminated rework inside the two merged columns.
		// The per-state chips carry the rest.
		hasReworkEdge() {
			return this.edgeState === "rework";
		},
	},
};
</script>

<style scoped lang="scss">
.kanban-card {
	border-top-color: var(--border-default) !important;
	border-right-color: var(--border-default) !important;
	border-bottom-color: var(--border-default) !important;
	// The 4px gutter is always reserved, so a rework card and its neighbours line
	// up. Without an edge it is just the card's own border at 4px, which is chrome
	// and not a workflow colour.
	border-left: 4px solid var(--border-default) !important;
	transition: box-shadow 0.2s ease;

	// The one state allowed a structural marker.
	&--rework {
		border-left-color: rgb(var(--v-theme-state-rework)) !important;
	}

	&--clickable {
		cursor: pointer;

		&:hover {
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
		}
	}
}
</style>
